/**
 * Texas Compliance API Service
 * Feature: F-268 (Epic E-049: Texas Frontend — Jurisdiction Switcher & Dashboard)
 * User Story: US-222 (Texas Compliance Dashboard Widgets)
 * Tasks: T-222-001, T-222-002, T-222-003
 */

import { landlordApi } from './api';

export interface DepositItem {
  id: string;
  tenantId: string;
  tenantName: string;
  propertyId: string;
  propertyName: string;
  unitNumber?: string;
  depositAmount: number;
  depositReference?: string;
  surrenderDate: string;
  deadlineDate: string;
  daysRemaining: number;
  status: 'overdue' | 'critical' | 'warning' | 'normal';
  refundStatus?: 'pending' | 'partially_refunded' | 'refunded' | 'disputed';
  deductionsAmount?: number;
}

export interface RekeyUnit {
  id: string;
  unitId: string;
  propertyId: string;
  propertyName: string;
  unitNumber: string;
  propertyManagerId?: string;
  propertyManagerName?: string;
  turnoverDate: string;
  rekeyDeadline: string;
  daysRemaining: number;
  status: 'overdue' | 'due_soon' | 'compliant';
  completed: boolean;
  completedDate?: string;
  contractorName?: string;
  keyTag?: string;
}

export interface RentalRegistration {
  id: string;
  propertyId: string;
  propertyName: string;
  address: string;
  jurisdictionType: 'HOUSTON_STR' | 'DALLAS_SFR';
  registrationNumber: string;
  issueDate: string;
  renewalDate: string;
  daysUntilRenewal: number;
  status: 'active' | 'expiring' | 'expired';
  inspectionStatus?: 'passed' | 'pending' | 'exempt';
  feePaid?: boolean;
}

export function calculateDepositStatus(surrenderDateStr: string): {
  deadlineDate: string;
  daysRemaining: number;
  status: 'overdue' | 'critical' | 'warning' | 'normal';
} {
  const surrenderDate = new Date(surrenderDateStr);
  const deadline = new Date(surrenderDate.getTime() + 30 * 86400000);
  const now = new Date();
  const msPerDay = 86400000;
  const daysRemaining = Math.round((deadline.getTime() - now.getTime()) / msPerDay);

  let status: 'overdue' | 'critical' | 'warning' | 'normal';
  if (daysRemaining < 0) {
    status = 'overdue';
  } else if (daysRemaining < 7) {
    status = 'critical';
  } else if (daysRemaining <= 14) {
    status = 'warning';
  } else {
    status = 'normal';
  }

  return {
    deadlineDate: deadline.toISOString().split('T')[0],
    daysRemaining,
    status,
  };
}

export function calculateRekeyStatus(turnoverDateStr: string, completed: boolean = false): {
  rekeyDeadline: string;
  daysRemaining: number;
  status: 'overdue' | 'due_soon' | 'compliant';
} {
  const turnoverDate = new Date(turnoverDateStr);
  const deadline = new Date(turnoverDate.getTime() + 7 * 86400000);
  const now = new Date();
  const daysRemaining = Math.round((deadline.getTime() - now.getTime()) / 86400000);

  if (completed) {
    return {
      rekeyDeadline: deadline.toISOString().split('T')[0],
      daysRemaining,
      status: 'compliant',
    };
  }

  let status: 'overdue' | 'due_soon' | 'compliant';
  if (daysRemaining < 0) {
    status = 'overdue';
  } else if (daysRemaining <= 3) {
    status = 'due_soon';
  } else {
    status = 'compliant';
  }

  return {
    rekeyDeadline: deadline.toISOString().split('T')[0],
    daysRemaining,
    status,
  };
}

export const texasComplianceService = {
  getDepositTracker: async (): Promise<DepositItem[]> => {
    try {
      const res = await landlordApi.get('api/texas/deposits');
      if (res?.data?.data && Array.isArray(res.data.data)) {
        return res.data.data;
      }
    } catch {
      try {
        const tenantRes = await landlordApi.get('api/tenants');
        const tenants = tenantRes?.data?.data || [];
        const items: DepositItem[] = [];

        tenants.forEach((t: any) => {
          if (t.depositAmount && (t.tenancyEndDate || t.surrenderDate)) {
            const surrender = t.surrenderDate || t.tenancyEndDate;
            const calc = calculateDepositStatus(surrender);
            items.push({
              id: 'dep-' + (t.id || t.tenantId),
              tenantId: t.id || t.tenantId,
              tenantName: t.name || (t.firstName + ' ' + (t.lastName || '')).trim() || 'Tenant',
              propertyId: t.propertyId || 'prop-default',
              propertyName: t.propertyName || 'Texas Property',
              unitNumber: t.unitNumber || t.unit,
              depositAmount: Number(t.depositAmount) || 0,
              depositReference: t.depositReference || ('DEP-' + t.id),
              surrenderDate: surrender,
              deadlineDate: calc.deadlineDate,
              daysRemaining: calc.daysRemaining,
              status: calc.status,
              refundStatus: t.refundStatus || 'pending',
            });
          }
        });

        if (items.length > 0) return items;
      } catch {
        // Fall through
      }
    }

    return [];
  },

  getRekeyStatus: async (propertyManagerId?: string): Promise<RekeyUnit[]> => {
    try {
      const res = await landlordApi.get('api/texas/rekey', {
        params: propertyManagerId ? { propertyManagerId } : undefined,
      });
      if (res?.data?.data && Array.isArray(res.data.data)) {
        return res.data.data;
      }
    } catch {
      // Fall through
    }

    return [];
  },

  getRentalRegistrations: async (): Promise<RentalRegistration[]> => {
    try {
      const res = await landlordApi.get('api/texas/dallas/registration/renewals');
      const list = res?.data?.data && Array.isArray(res.data.data) ? res.data.data : [];
      if (list.length > 0) {
        return list.map((r: any) => {
          const rec = r.renewal || {};
          return {
            id: r.propertyId || r.id || 'renewal-' + Math.random().toString(36).slice(2),
            propertyId: r.propertyId || r.id,
            propertyName: r.propertyName || r.propertyAddress || 'Dallas Property',
            address: r.propertyAddress || r.address || '',
            jurisdictionType: 'DALLAS_SFR',
            registrationNumber: r.registrationNumber || r.registrationNum || '',
            issueDate: r.issuedDate || r.issueDate || '',
            renewalDate: r.expiryDate || rec.expiryDate || '',
            daysUntilRenewal: rec.daysUntilExpiry ?? (rec.daysUntilExpiry === null ? -999 : 0),
            status:
              rec.status === 'EXPIRED' || rec.isExpired
                ? 'expired'
                : (rec.status === 'ACTIVE' ? 'active' : 'expiring'),
            inspectionStatus: r.passedInspection ? 'passed' : 'pending',
            feePaid: r.feePaid ?? r.registrationFeePaid ?? false,
          } as RentalRegistration;
        });
      }
    } catch {
      try {
        const dallasRes = await landlordApi.get('api/texas/dallas/registration/summary');
        if (dallasRes?.data?.data?.registrations) {
          return dallasRes.data.data.registrations;
        }
      } catch {
        // Fall through
      }
    }

    return [];
  },
};
